import { Badge, Dropdown, Flex, List, MenuProps, Popover, Tooltip } from "antd";
import { Conversation } from "../../../interfaces/conversations.type";
import {  HeadingText } from "../../generic/Typography";
import { UserAddOutlined } from "@ant-design/icons";
import { HiDotsVertical } from "react-icons/hi";
import { Content } from "antd/es/layout/layout";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store/store";
import { setSelectedConversation } from "../../../store/slices/conversation.slice";
import { Typography } from "antd";
import assign from "../../../assets/images/icon/Assign.svg";
import resolveImg from "../../../assets/images/icon/Check-circle.svg";
import { memo, useMemo, useState } from "react";
import ModalWrapper from "../../generic/modal";
import { UsersList } from "../didUsers/user.list";
import { IoIosCheckmarkCircle } from "react-icons/io";
import { RxCrossCircled } from "react-icons/rx";
import { FiUserMinus } from "react-icons/fi";
import { formatMessageDate } from "../../../utils/messageDateFormat";
import { updateConversation } from "../../../hooks/apis/conversations.api";
import { ConversationAvatar } from "./ConversationAvatar";

const { Text } = Typography;
const boxStyle: React.CSSProperties = {
    padding: "5px",
    border: "none",
    position: "relative",
    cursor: "pointer"
};

const ItemContentStyle: React.CSSProperties = {
    width: "100%",
    padding: "5px",
    borderRadius: "10px"
};
const ConversationContentStyle: React.CSSProperties = { width: "50%" };
const assignedUserStyle: React.CSSProperties = {
    color: "blue",
    height: "20px",
    width: "20px"
};
const dotsWrapperStyle: React.CSSProperties = {
    height: "100%",
    top: "0px",
    right: "5px",
    position: "absolute"
};
const dotsStyle: React.CSSProperties = { color: "gray" };

const conversationDateStyle: React.CSSProperties = {
    fontWeight: "500",
    lineHeight: "20px",
    color: "rgb(119, 117, 135)",
    fontSize: "14px",
    minWidth: "85px",
    textAlign: "center"
};

const conversationMessageStyle: React.CSSProperties = {
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: "20px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "rgb(119, 117, 135)"
};
const ParticipantDetails = ({ labels }: { labels: string[] }) => (
    <Flex vertical gap={4} style={{ minWidth: "220px" }}>
        {labels.map((label) => (
            <Text key={label} style={{ fontSize: "12px" }}>
                {label}
            </Text>
        ))}
    </Flex>
);

const ConversationsCard = memo(({
    conversation,
    index,
    isSelected,
    setIsNewConversation,
    assigned,
    convName,
    additionalParticipantCount,
    participantLabels,
    didUsers
}: {
    conversation: Conversation;
    index: number;
    isSelected: boolean;
    setIsNewConversation: Function;
    assigned: string | null;
    convName: string | null | undefined;
    additionalParticipantCount: number;
    participantLabels: string[];
    didUsers: { id: string; first_name: string; last_name: string }[];
}) => {
    const dispatch: AppDispatch = useDispatch();
    const didUsersMap = useMemo(() => {
        if (!didUsers || didUsers.length === 0) {
            return new Map<string, string>();
        }
        const map = new Map<string, string>();
        didUsers.forEach(user => {
            map.set(user.id, `${user.first_name} ${user.last_name}`);
        });
        return map;
    }, [didUsers]);

    const [isAssignConversation, setIsAssignConversation] = useState(false);

    const onClickHandler = (conversation: Conversation) => {
        dispatch(
            setSelectedConversation({
                conversation_id: conversation.id,
                did: conversation.did,
                participants: conversation.participants,
                type: conversation.type,
                total_unread: conversation.unread_messages_count,
                unread: conversation.unread,
                unread_messages_count: conversation.unread_messages_count,
            })
        );
        setIsNewConversation(false);
    };
    const conversationMenu: MenuProps["items"] = [
        {
            key: "1",
            label: "Assign",
            icon: <img src={assign} alt="assign" style={{ height: "20px", width: "20px" }} />,
            onClick: () => {
                setIsAssignConversation(true);
            }
        },
        {
            key: "2",
            label: conversation.resolved ? "Unresolve" : "Resolve",
            icon: conversation.resolved ? (
                <RxCrossCircled style={{ height: "20px", width: "20px" }} />
            ) : (
                <img
                    src={resolveImg}
                    alt="resolve"
                    style={{ height: "20px", width: "20px", opacity: conversation.resolved ? "0.2" : "1" }}
                />
            ),
            onClick: () => {
                updateConversation({
                    did: conversation.did,
                    conversation_id: conversation.id,
                    resolved_at: conversation.resolved ? null : new Date()
                });
            }
        },
        {
            key: "3",
            label: "Unassign",
            disabled: conversation.assigned_user === null,
            icon: <FiUserMinus style={{ height: "20px", width: "20px" }} fontWeight="700" />,
            onClick: () => {
                updateConversation({ did: conversation.did, conversation_id: conversation.id, assigned_user: null });
            }
        }
    ];

    return (
        <Content key={index}>
            {isAssignConversation && (
                <ModalWrapper title={"Assign"} setIsModalOpen={setIsAssignConversation} isModalOpen={isAssignConversation}>
                    <UsersList
                        updateConversation={updateConversation}
                        setIsModalOpen={setIsAssignConversation}
                        conversation={{
                            did: conversation.did,
                            conversation_id: conversation.id
                        }}
                    />
                </ModalWrapper>
            )}
            <List.Item style={boxStyle}>
                <Flex style={{ width: "100%" }}>
                    <Flex
                        onClick={() => onClickHandler(conversation)}
                        align="flex-start"
                        justify="space-evenly"
                        style={{
                            ...ItemContentStyle,
                            background: isSelected ? "#eff4fb" : "#ffff"
                        }}
                        wrap="wrap"
                        gap="small"
                    >
                        <ConversationAvatar participants={conversation.participants} />
                        <Flex gap={"small"} style={ConversationContentStyle} vertical={true}>
                            <Flex gap={8} style={{ minWidth: 0 }}>
                                <Popover
                                    content={<ParticipantDetails labels={participantLabels} />}
                                    trigger="hover"
                                >
                                    <span style={{ flex: "1 1 0", minWidth: 0 }}>
                                        <HeadingText
                                            text={convName || "Conversation"}
                                            style={{ display: "block" }}
                                        />
                                    </span>
                                </Popover>
                                {additionalParticipantCount > 0 && (
                                    <Popover
                                        content={<ParticipantDetails labels={participantLabels.slice(2)} />}
                                        trigger="hover"
                                    >
                                        <Text
                                            style={{
                                                cursor: "help",
                                                flex: "0 0 auto",
                                                fontSize: "14px",
                                                fontWeight: 500,
                                                lineHeight: "20px",
                                            }}
                                        >
                                            +{additionalParticipantCount}
                                        </Text>
                                    </Popover>
                                )}
                                {assigned && (
                                    <Tooltip
                                        title={`Assigned to ${didUsersMap.get(assigned)}`}
                                        color={"#108ee9"}
                                    >
                                        <UserAddOutlined style={assignedUserStyle} />
                                    </Tooltip>
                                )}
                                {conversation.resolved && (
                                    <Tooltip title="Resolved" color={"#108ee9"}>
                                        <IoIosCheckmarkCircle style={assignedUserStyle} />
                                    </Tooltip>
                                )}
                            </Flex>
                            <Text style={conversationMessageStyle}>{conversation.last_message}</Text>
                        </Flex>
                        <Flex align="center" vertical={true}>
                            <Typography.Text style={conversationDateStyle}>
                                {formatMessageDate(new Date(conversation.last_message_time))}
                            </Typography.Text>
                            {Number(conversation.unread_messages_count)
                                ? Number(conversation.unread_messages_count) > 0 && (
                                      <Badge count={`${Number(conversation.unread_messages_count)}`.padStart(2, "0")} showZero={false} color="#653BFA" />
                                  )
                                : conversation.unread &&
                                  Number(conversation.unread_messages_count) === 0 && <Badge text=" " showZero={false} color="#653BFA" />}
                        </Flex>
                    </Flex>
                    <Flex style={dotsWrapperStyle} justify="center" align="center">
                        <Dropdown menu={{ items: conversationMenu }} trigger={["click"]}>
                            <HiDotsVertical style={dotsStyle} />
                        </Dropdown>
                    </Flex>
                </Flex>
            </List.Item>
        </Content>
    );
});

export default ConversationsCard;
